<script setup lang="ts">
  // POS V2 main page. Ported from cartadigital-pos app/pages/pos/index.vue;
  // waiter calls / printing queue / realtime indicator / settings toolbar are
  // the battle-tested old-POS components reused as-is.
  import { computed, ref, watchEffect } from 'vue';
  import {
    breakpointsTailwind,
    useBreakpoints,
    refDebounced,
  } from '@vueuse/core';
  import { useRouter } from 'vue-router/composables';
  import { OrderType } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { IPosTableView } from '@/interfaces/IPosTableView';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import { useMenuStore } from '@/composition/use-menu';
  import { usePosMainMode } from '@/composition/use-pos-main-mode';
  import { usePosNewOrder } from '@/composition/use-pos-new-order';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import RestaurantPosCallsDropdown from '@/components/pages/restaurant/pos/RestaurantPosCallsDropdown.vue';
  import RestaurantPosNewOrdersDropdown from '@/components/pages/restaurant/pos/RestaurantPosNewOrdersDropdown.vue';
  import RestaurantPosPrintingDropdown from '@/components/pages/restaurant/pos/RestaurantPosPrintingDropdown.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosV2CajaChip from '@/components/pos-v2/main/PosV2CajaChip.vue';
  import PosV2Header from '@/components/pos-v2/main/PosV2Header.vue';
  import PosV2IncomingPane from '@/components/pos-v2/main/PosV2IncomingPane.vue';
  import PosV2OrdersList from '@/components/pos-v2/main/PosV2OrdersList.vue';
  import PosV2TablesGrid from '@/components/pos-v2/main/PosV2TablesGrid.vue';
  import PosV2OrderPreviewModal from '@/components/pos-v2/modals/PosV2OrderPreviewModal.vue';
  import PosV2TableOrdersPreviewModal from '@/components/pos-v2/modals/PosV2TableOrdersPreviewModal.vue';

  const modals = useModal();
  const ordersHelper = useOrders();
  const ordersStore = useOrdersStore();
  const menuStore = useMenuStore();
  const restaurantStore = useRestaurantStore();
  const router = useRouter();
  const { isWhatsAppMode, navigateToNewOrder } = usePosNewOrder();

  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isLargeScreen = breakpoints.greaterOrEqual('lg');

  //#region Mode selection

  // Shared across navigations: coming back from an order/table page restores
  // the tab the user was on instead of resetting to "Tables".
  const { mainMode: mode } = usePosMainMode();

  function pendingCount(type: OrderType): number {
    return ordersStore.pendingOrdersByType?.[type]?.length ?? 0;
  }

  /**
   * A mode is usable when its flow is enabled OR it still has pending orders.
   * While availability is still unknown (stores loading) treat it as enabled
   * so the page shows loading skeletons instead of "flow disabled".
   */
  function isModeEnabled(type: OrderType): boolean {
    if (!menuStore.availableOrderTypes || !ordersStore.pendingOrdersByType) {
      return true;
    }

    const available = menuStore.availableOrderTypes.some(
      (t) => t.type === type
    );
    return available || pendingCount(type) > 0;
  }

  /** Ported -pos resolveDefaultMode: fall back to the first enabled mode. */
  function resolveDefaultMode(preferred: OrderType): OrderType {
    const priority: OrderType[] = [
      OrderType.Seat,
      OrderType.Away,
      OrderType.Delivery,
    ];

    const normalizedPreferred = priority.includes(preferred)
      ? preferred
      : priority[0];

    if (isModeEnabled(normalizedPreferred)) return normalizedPreferred;

    return priority.find((t) => isModeEnabled(t)) ?? priority[0];
  }

  watchEffect(() => {
    const resolved = resolveDefaultMode(mode.value);
    if (resolved !== mode.value) {
      mode.value = resolved;
    }
  });

  //#endregion

  //#region Search

  const search = ref('');
  const debouncedSearch = refDebounced(search, 200);

  //#endregion

  //#region Pane actions

  const showSeatPane = computed(
    () => mode.value === OrderType.Seat && isModeEnabled(OrderType.Seat)
  );

  function openTable(table: IPosTableView) {
    router.push(
      `/${restaurantStore.restaurant.stringId}/pos/table/${table.id}`
    );
  }

  function goToHistory() {
    router.push(`/${restaurantStore.restaurant.stringId}/pos/history`);
  }

  function handleOrderClick(order: IOrder) {
    ordersHelper.openOrder(order);
  }

  /** Long-press / right-click on a floor-plan tile: read-only orders preview. */
  function previewTable(table: IPosTableView) {
    modals.showDynamicDialog(
      PosV2TableOrdersPreviewModal,
      { tableId: table.id },
      'pos-v2-table-orders-preview',
      // clickToClose off: on touch, the click synthesized when the finger
      // lifts after the long-press would land on the overlay and instantly
      // dismiss the modal.
      { width: 1100, clickToClose: false }
    );
  }

  /** Long-press / right-click on an order card: read-only order preview. */
  function previewOrder(order: IOrder) {
    modals.showDynamicDialog(
      PosV2OrderPreviewModal,
      { orderId: order.id },
      'pos-v2-order-preview',
      // clickToClose off: same touch-release reason as previewTable.
      { width: 1100, clickToClose: false }
    );
  }

  //#endregion
</script>

<template>
  <div class="pos-v2-page">
    <PosV2Header :mode.sync="mode" :search.sync="search" class="page-header">
      <template #tools>
        <PosV2CajaChip />
        <PosUiButton
          color="neutral"
          variant="ghost"
          size="md"
          icon="history"
          class="history-button"
          :aria-label="$t('posV2.history')"
          :title="$t('posV2.history')"
          @click="goToHistory"
        />
        <RestaurantPosNewOrdersDropdown v-if="!isLargeScreen" />
        <RestaurantPosCallsDropdown />
        <RestaurantPosPrintingDropdown />
      </template>
    </PosV2Header>

    <div class="main-content">
      <div class="left-pane">
        <PosV2TablesGrid
          v-if="showSeatPane"
          :search="debouncedSearch"
          @table-click="openTable"
          @table-preview="previewTable"
          @clear-search="search = ''"
        />
        <PosV2OrdersList
          v-else-if="mode !== OrderType.Seat"
          :order-type="mode"
          :search="debouncedSearch"
          @order-click="handleOrderClick"
          @order-preview="previewOrder"
          @clear-search="search = ''"
        />
        <PosUiEmptyState
          v-else
          icon="alert-circle-outline"
          :title="$t('posV2.flowDisabledTitle')"
          :description="$t('posV2.flowDisabledDescription')"
          class="disabled-flow"
        />
      </div>

      <div v-if="isLargeScreen" class="right-pane">
        <PosV2IncomingPane class="right-pane-incoming" />
      </div>
    </div>

    <!-- Thumb-reachable creation on phones (header button hidden < md) -->
    <button
      v-if="!isWhatsAppMode"
      type="button"
      class="mobile-new-order"
      @click="navigateToNewOrder(mode)"
    >
      <PosUiIcon name="plus" class="mobile-new-order-icon" />
      {{ $t('posV2.newOrder') }}
    </button>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-page {
    @apply tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-col tw-bg-canvas;

    .page-header {
      @apply tw-flex-none;
    }

    .main-content {
      @apply tw-flex tw-min-h-0 tw-flex-1;

      .left-pane {
        @apply tw-min-w-0 tw-flex-1 tw-overflow-hidden;

        .disabled-flow {
          @apply tw-h-full;
        }
      }

      .right-pane {
        @apply tw-flex tw-w-[22rem] tw-flex-none tw-flex-col tw-border-l tw-border-neutral-200 tw-bg-white xl:tw-w-96;

        .right-pane-incoming {
          @apply tw-min-h-0 tw-flex-1;
        }
      }
    }

    .mobile-new-order {
      @apply tw-flex tw-min-h-[56px] tw-flex-none tw-items-center tw-justify-center tw-gap-2 tw-bg-orange-600 tw-text-base tw-font-semibold tw-text-white tw-transition-colors active:tw-bg-orange-700 md:tw-hidden;
      padding-bottom: env(safe-area-inset-bottom);

      .mobile-new-order-icon {
        @apply tw-text-xl;
      }
    }
  }
</style>
